/**
 * Toast notifications — module-level singleton queue.
 *
 * Originally every "success" announcement (refresh complete, promote
 * complete) only fired through sr-only aria-live regions, leaving
 * sighted users to infer success from the list refreshing. Toasts
 * surface the same string both visibly AND audibly through one
 * aria-live region, so the parallel sr-only blocks can go away.
 *
 * Singleton on purpose: any composable / view can push without
 * prop-drilling a controller through the layout. The viewport
 * (<ToastViewport />) is mounted once in the app shell (layouts/AppLayout) and reads
 * from the same ref.
 *
 * Usage:
 *   const toasts = useToasts();
 *   toasts.success("Promoted v1.2.3 to beta.");
 *   toasts.error("Refresh failed: 503.");
 *   toasts.info("Loaded 42 builds.");
 *
 * Errors are intentionally lower-volume in this codebase — most action
 * errors render *inside* the originating dialog (so the operator can
 * fix and retry in place), so toasts.error() is reserved for "fire and
 * forget" failures with no inline owner.
 */
import { ref } from "vue";

export type ToastKind = "success" | "error" | "info";

export interface Toast {
  /** Stable id used as v-for key. Monotonically increasing. */
  id: number;
  kind: ToastKind;
  message: string;
}

const DEFAULT_DISMISS_MS = 5_000;

const _toasts = ref<Toast[]>([]);
const _timers = new Map<number, number>();
let _nextId = 1;

function _scheduleDismiss(id: number, ms: number) {
  if (_timers.has(id)) window.clearTimeout(_timers.get(id)!);
  const handle = window.setTimeout(() => dismiss(id), ms);
  _timers.set(id, handle);
}

function dismiss(id: number) {
  const idx = _toasts.value.findIndex((t) => t.id === id);
  if (idx >= 0) _toasts.value.splice(idx, 1);
  const handle = _timers.get(id);
  if (handle !== undefined) {
    window.clearTimeout(handle);
    _timers.delete(id);
  }
}

function push(kind: ToastKind, message: string, dismissAfterMs = DEFAULT_DISMISS_MS): number {
  const id = _nextId++;
  _toasts.value.push({ id, kind, message });
  if (dismissAfterMs > 0) _scheduleDismiss(id, dismissAfterMs);
  return id;
}

/**
 * Return the singleton toast controller. Every call returns the same
 * underlying queue — this is just for ergonomic destructuring at
 * call sites.
 */
export function useToasts() {
  return {
    toasts: _toasts,
    push,
    dismiss,
    success: (msg: string, ms?: number) => push("success", msg, ms),
    error: (msg: string, ms?: number) => push("error", msg, ms),
    info: (msg: string, ms?: number) => push("info", msg, ms),
  };
}
